import {
  ComposerPrimitive,
  ThreadPrimitive,
  useAssistantState,
  useAui,
} from "@assistant-ui/react";
import {
  Box,
  Flex,
  Icon,
  Menu,
  MenuItem,
  MenuList,
  Text,
} from "@chakra-ui/react";
import React, { ReactElement } from "react";
import { BsArrowReturnLeft } from "react-icons/bs";
import {
  HiOutlineBookOpen,
  HiOutlineChevronDown,
  HiOutlineStopCircle,
} from "react-icons/hi2";

import { Button, MenuButton } from "../../../../components";
import { useCustomInsightsChat } from "./CustomInsightsChatProvider";
import {
  type ThreadMessage,
  isSetFiltersMessage,
} from "./customInsightsChatUtils";
import { BH_LIBRARY_TEMPLATES } from "./CustomInsightsSidebar";
import { AssistantMessage, UserMessage } from "./InsightsChatMessages";
import {
  InsightsWelcomeContent,
  RerunReportResults,
} from "./InsightsWelcomeContent";
import { RenderChartToolUI } from "./RenderChartToolUI";

function InsightsChatColumn({
  filtersOpen,
}: {
  filtersOpen: boolean;
}): ReactElement {
  const aui = useAui();
  const isRunning = useAssistantState((s) => s.thread.isRunning);
  const { hasPreviewInterviews, isPreviewLoading } = useCustomInsightsChat();
  const messages = useAssistantState((s) => s.thread.messages) as
    | ThreadMessage[]
    | undefined;
  const lastUserMessage = messages
    ? [...messages].reverse().find((m) => m.role === "user")
    : undefined;
  const isFilterRun =
    isRunning &&
    !!lastUserMessage?.content &&
    isSetFiltersMessage(lastUserMessage.content);
  const composerDisabled = isPreviewLoading || !hasPreviewInterviews;
  const disabledMessage = isPreviewLoading
    ? "Checking matching interviews..."
    : "No matching interviews for the current filters. Update them to enable chat.";

  return (
    <Flex
      display={filtersOpen ? "none" : undefined}
      flexDir="column"
      flex={1}
      px="16px"
      minW={0}
    >
      <ThreadPrimitive.Root
        style={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minHeight: 0,
          width: "100%",
        }}
      >
        <ThreadPrimitive.Viewport
          style={{
            overflowY: "auto",
            flex: 1,
            minHeight: 0,
            paddingBottom: 24,
            placeContent: "end",
          }}
        >
          <InsightsWelcomeContent />
          <RerunReportResults />
          <ThreadPrimitive.Messages
            components={{ UserMessage, AssistantMessage }}
          />
          <ThreadPrimitive.ScrollToBottom />
        </ThreadPrimitive.Viewport>
        <Box flexShrink={0} pb="16px" w="100%">
          <Box
            borderRadius="12px"
            background="linear-gradient(143deg, rgba(0,116,221,0.5) 3%, rgba(89,30,255,0.5) 59%, rgba(150,68,255,0.5) 126%)"
            boxShadow="5px 5px 15px rgba(27,26,45,0.1)"
            px="12px"
            py="10px"
            w="100%"
          >
            <Flex flexDir="column" gap="12px">
              <Box pr="48px">
                <Menu matchWidth>
                  <MenuButton
                    as={Box}
                    bg="white"
                    border="1px solid"
                    borderColor="gray.200"
                    borderRadius="6px"
                    h="40px"
                    cursor={
                      isRunning || composerDisabled ? "not-allowed" : "pointer"
                    }
                    overflow="hidden"
                    _hover={{ borderColor: "gray.300" }}
                    w="100%"
                  >
                    <Flex alignItems="center" px="12px" gap="8px" h="100%">
                      <Icon
                        as={HiOutlineBookOpen}
                        boxSize="24px"
                        color="gray.600"
                        flexShrink={0}
                      />
                      <Text
                        flex={1}
                        fontSize="14px"
                        lineHeight="20px"
                        color="gray.600"
                        noOfLines={1}
                      >
                        Select from BrightHire&apos;s question library...
                      </Text>
                      <Icon
                        as={HiOutlineChevronDown}
                        boxSize="24px"
                        color="gray.500"
                        flexShrink={0}
                      />
                    </Flex>
                  </MenuButton>
                  <MenuList maxH="300px" overflowY="auto">
                    {BH_LIBRARY_TEMPLATES.map((template) => (
                      <MenuItem
                        key={template.title}
                        fontSize="sm"
                        isDisabled={isRunning || composerDisabled}
                        onClick={() => {
                          if (isRunning || composerDisabled) {
                            return;
                          }
                          aui.thread().append({
                            role: "user",
                            content: [
                              {
                                type: "text",
                                text: `[SKILL:${template.skillName}] ${template.description} Return the following outputs: ${template.output}`,
                              },
                            ],
                          });
                        }}
                      >
                        {template.title}
                      </MenuItem>
                    ))}
                  </MenuList>
                </Menu>
              </Box>
              <ComposerPrimitive.Root
                style={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  width: "100%",
                  gap: "8px",
                }}
              >
                <Flex
                  bg={composerDisabled ? "gray.50" : "white"}
                  border="1px solid"
                  borderColor={composerDisabled ? "gray.300" : "gray.200"}
                  borderRadius="6px"
                  alignItems="center"
                  px="16px"
                  py="8px"
                  gap="8px"
                  flex={1}
                  h="48px"
                  overflow="hidden"
                >
                  <Box
                    flex={1}
                    overflow="hidden"
                    sx={{
                      "& textarea, & input": {
                        width: "100%",
                        resize: "none",
                        bg: "transparent",
                        border: "none",
                        outline: "none",
                        p: "0",
                        fontFamily: "inherit",
                        fontSize: "14px",
                        lineHeight: "20px",
                        color: "gray.800",
                        _placeholder: { color: "gray.400" },
                        "&:focus": { outline: "none", boxShadow: "none" },
                      },
                    }}
                  >
                    {composerDisabled ? (
                      <Text fontSize="14px" lineHeight="20px" color="gray.500">
                        {disabledMessage}
                      </Text>
                    ) : (
                      <ComposerPrimitive.Input placeholder="Ask a custom question.." />
                    )}
                  </Box>
                </Flex>
                {isRunning ? (
                  <Button
                    onClick={() => aui.thread().cancelRun()}
                    isDisabled={isFilterRun}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 40,
                      height: 40,
                      flexShrink: 0,
                      borderRadius: 8,
                      backgroundColor: "transparent",
                      color: "white",
                      border: "none",
                      cursor: isFilterRun ? "not-allowed" : "pointer",
                    }}
                  >
                    <Icon as={HiOutlineStopCircle} boxSize="24px" />
                  </Button>
                ) : composerDisabled ? (
                  <Button
                    isDisabled
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 40,
                      height: 40,
                      flexShrink: 0,
                      borderRadius: 8,
                      backgroundColor: "transparent",
                      color: "white",
                      border: "none",
                      cursor: "not-allowed",
                      opacity: 0.5,
                    }}
                  >
                    <Icon as={BsArrowReturnLeft} boxSize="24px" />
                  </Button>
                ) : (
                  <ComposerPrimitive.Send
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 40,
                      height: 40,
                      flexShrink: 0,
                      borderRadius: 8,
                      backgroundColor: "transparent",
                      color: "white",
                      border: "none",
                      cursor: "pointer",
                    }}
                  >
                    <Icon as={BsArrowReturnLeft} boxSize="24px" />
                  </ComposerPrimitive.Send>
                )}
              </ComposerPrimitive.Root>
              {composerDisabled && (
                <Text
                  fontSize="12px"
                  lineHeight="16px"
                  color={isPreviewLoading ? "gray.500" : "red.600"}
                  px="4px"
                >
                  {disabledMessage}
                </Text>
              )}
            </Flex>
          </Box>
        </Box>
      </ThreadPrimitive.Root>
    </Flex>
  );
}

export function InsightsChatInner({
  filtersOpen,
}: {
  filtersOpen: boolean;
}): ReactElement {
  return (
    <>
      <RenderChartToolUI />
      <InsightsChatColumn filtersOpen={filtersOpen} />
    </>
  );
}
